import React, { useCallback, useEffect, useRef } from 'react';

import { cssMap } from '@atlaskit/css';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { TOOLBAR_RANK, TOOLBARS, VIEW_MODE_TOGGLE_SECTION } from '@atlaskit/editor-common/toolbar';
import type { ReadonlyTransaction } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import type { RegisterComponent } from '@atlaskit/editor-toolbar-model';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type {
	MarkdownModePlugin,
	MarkdownModePluginOptions,
	MarkdownModePluginState,
	MarkdownModeView,
} from './markdownModePluginType';
import { markdownModePluginKey } from './pm-plugins/main';
import { MarkdownModeContentComponent } from './ui/MarkdownModeContentComponent';
import { MarkdownModeViewToggle } from './ui/MarkdownModeViewToggle';

/**
 * Wrapper that calls the consumer-supplied `useViewMode` hook (e.g.
 * Confluence's sweet-state `useMarkdownModeView`) and passes the result
 * into `MarkdownModeContentComponent`. It also syncs the external view
 * state into ProseMirror plugin state so that other editor components
 * (e.g. `FullPageContentArea`) can read it via `useSharedPluginStateSelector`.
 *
 * The view-to-PM sync is built inside this React component (rather than at
 * the plugin layer) so the callback can be `useCallback`-memoised against
 * `editorView` — keeping the prop reference stable across renders and
 * letting React skip downstream effect re-runs.
 */
const MarkdownModeContentComponentWrapper = ({
	editorView,
	config,
}: {
	config: MarkdownModePluginOptions;
	editorView: EditorView;
}) => {
	const defaultUseViewMode = (): MarkdownModeView => 'wysiwyg';
	const useViewMode = config.useViewMode ?? defaultUseViewMode;
	const currentView = useViewMode();

	const syncViewToPM = useCallback(
		(view: MarkdownModeView) => {
			const tr = editorView.state.tr;
			tr.setMeta(markdownModePluginKey, { view });
			editorView.dispatch(tr);
		},
		[editorView],
	);

	// Sync the external (sweet-state) view into ProseMirror plugin state
	// so that FullPageContentArea can read it via shared state.
	const prevViewRef = useRef<MarkdownModeView>(currentView);
	useEffect(() => {
		if (currentView !== prevViewRef.current) {
			prevViewRef.current = currentView;
			syncViewToPM(currentView);
		}
	}, [currentView, syncViewToPM]);

	// Also sync on first mount if view is not wysiwyg.
	useEffect(() => {
		if (currentView !== 'wysiwyg') {
			syncViewToPM(currentView);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<MarkdownModeContentComponent
			editorView={editorView}
			config={config}
			currentView={currentView}
		/>
	);
};

const styles = cssMap({
	trailingSection: {
		position: 'absolute',
		insetInlineEnd: token('space.150'),
		insetBlockStart: '50%',
		transform: 'translateY(-50%)',
		display: 'flex',
		alignItems: 'center',
	},
});

const buildToolbarComponents = (
	config: MarkdownModePluginOptions | undefined,
	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	api: any,
): RegisterComponent[] => {
	if (!config?.isMarkdownMode) {
		return [];
	}

	// If a custom toggle component is provided, use it; otherwise fall
	// back to the built-in MarkdownModeViewToggle that reads plugin state.
	const ToggleComponent = config.toggleComponent;

	return [
		{
			type: VIEW_MODE_TOGGLE_SECTION.type,
			key: VIEW_MODE_TOGGLE_SECTION.key,
			parents: [
				{
					type: 'toolbar',
					key: TOOLBARS.PRIMARY_TOOLBAR,
					rank: TOOLBAR_RANK[VIEW_MODE_TOGGLE_SECTION.key],
				},
			],
			component: () => (
				<Box xcss={styles.trailingSection} testId="markdown-mode-view-toggle-section">
					{ToggleComponent ? <ToggleComponent /> : <MarkdownModeViewToggle api={api} />}
				</Box>
			),
		},
	];
};

export const markdownModePlugin: MarkdownModePlugin = ({ config, api }) => {
	let editorView: EditorView | undefined;
	const setEditorView = (newEditorView: EditorView) => {
		editorView = newEditorView;
	};

	api?.toolbar?.actions.registerComponents(buildToolbarComponents(config, api));

	return {
		name: 'markdownMode',

		getSharedState(editorState) {
			// `sharedState` is typed as non-undefined on `MarkdownModePlugin`,
			// so we surface a default state when no editor state exists yet
			// (e.g. before the editor view has mounted). This matches the
			// pattern used by `editor-plugin-paste` and lets consumers read
			// the state without an extra null-check.
			if (!editorState) {
				return {
					view: 'wysiwyg',
					isMarkdownMode: config?.isMarkdownMode ?? false,
				};
			}
			return (
				markdownModePluginKey.getState(editorState) ?? {
					view: 'wysiwyg',
					isMarkdownMode: config?.isMarkdownMode ?? false,
				}
			);
		},

		actions: {
			setView(view: MarkdownModeView) {
				if (!editorView) {
					return;
				}
				const tr = editorView.state.tr;
				tr.setMeta(markdownModePluginKey, { view });
				editorView.dispatch(tr);
			},
		},

		contentComponent({ editorView: contentEditorView }) {
			if (!config?.isMarkdownMode || !contentEditorView) {
				return null;
			}
			return <MarkdownModeContentComponentWrapper editorView={contentEditorView} config={config} />;
		},

		pmPlugins() {
			return [
				{
					name: 'markdownMode',
					plugin: () => {
						return new SafePlugin<MarkdownModePluginState>({
							key: markdownModePluginKey,
							state: {
								init: () => ({
									view: 'wysiwyg',
									isMarkdownMode: config?.isMarkdownMode ?? false,
								}),
								apply(tr: ReadonlyTransaction, currentState: MarkdownModePluginState) {
									const meta = tr.getMeta(markdownModePluginKey) as
										| { view: MarkdownModeView }
										| undefined;
									if (meta) {
										return { ...currentState, view: meta.view };
									}
									return currentState;
								},
							},
							view(newEditorView: EditorView) {
								setEditorView(newEditorView);
								return {};
							},
						});
					},
				},
			];
		},
	};
};
